import { Box, CloseButton, Tabs, Text } from "@mantine/core";
import { useState } from "react";

import type { VhdpPlanData } from "./vhdpPlanData";

import { DevicesTab } from "./DevicesTab";
import styles from "./PlanDetails.module.css";
import { PlanTab } from "./PlanTab";

export type PlanDetailsTab = "devices" | "plan";

export interface PlanDetailsProps {
    data: VhdpPlanData;
    defaultTab?: PlanDetailsTab;
    onClose?: () => void;
}

/**
 * The "View plan details" surface — the in-app replacement for the printed VHDP reference sheet
 * (openspec long-form-ai). Two tabs: the searchable device/service-fee matrix and the plan summary.
 *
 * Self-contained and layout-independent: it fills its parent (height 100%), owns its own scrolling
 * body under a sticky header, and carries the close affordance. `PlanDetailsModal` wraps it in a
 * Mantine Modal for the real "View plan details" trigger; stories render it inside a sized frame.
 */
export const PlanDetails = ({ data, defaultTab = "devices", onClose }: PlanDetailsProps) => {
    const [tab, setTab] = useState<string | null>(defaultTab);

    return (
        <Tabs
            value={tab}
            onChange={setTab}
            color="asurion"
            keepMounted={false}
            classNames={{ root: styles.root, list: styles.tabList, tab: styles.tab }}
        >
            <Box className={styles.stickyTop}>
                <header className={styles.header}>
                    <Text component="h2" className={styles.title}>
                        {data.meta.productName}
                    </Text>
                    <CloseButton aria-label="Close plan details" onClick={onClose} />
                </header>
                <Tabs.List>
                    <Tabs.Tab value="devices">Devices &amp; service fees</Tabs.Tab>
                    <Tabs.Tab value="plan">Plan details</Tabs.Tab>
                </Tabs.List>
            </Box>

            <Box className={styles.body}>
                <Tabs.Panel value="devices">
                    <DevicesTab data={data} />
                </Tabs.Panel>
                <Tabs.Panel value="plan">
                    <PlanTab data={data} />
                </Tabs.Panel>
            </Box>
        </Tabs>
    );
};
